import { Play, Pause, SkipBack, SkipForward, RefreshCw, AlertCircle } from "lucide-react";
import { useRef, useState, useEffect, useCallback } from "react";
import { adjustColor } from "@/lib/gift-utils";
import { trackEvent } from "@/lib/analytics";

interface GiftPlayerCardProps {
  trackTitle: string;
  heroName?: string;
  audioUrl: string;
  coverUrl?: string | null;
  accentColor?: string;
  isDark?: boolean;
  coverGradient?: string;
  onFirstPlay?: () => void;
  onPlayCountChange?: (count: number) => void;
  onCompletionChange?: (percent: number) => void;
}

const GiftPlayerCard = ({
  trackTitle,
  heroName,
  audioUrl,
  coverUrl,
  accentColor = "#6C63FF",
  isDark,
  coverGradient,
  onFirstPlay,
  onPlayCountChange,
  onCompletionChange,
}: GiftPlayerCardProps) => {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [showPulse, setShowPulse] = useState(true);
  const [blobSrc, setBlobSrc] = useState<string | null>(null);
  const hasCalledFirstPlay = useRef(false);
  const internalPlayCount = useRef(0);
  const triedBlobFallback = useRef(false);
  const [audioError, setAudioError] = useState<string | null>(null);
  const [isRepairing, setIsRepairing] = useState(false);
  const repairAttempts = useRef(0);

  const accent = isDark ? "#D4AF37" : accentColor;

  // Health check: verify audio URL is accessible
  useEffect(() => {
    if (!audioUrl) {
      setAudioError("no_url");
      console.error("[GiftPlayer] Audio URL is empty/undefined!");
      trackEvent("gift_audio_play_error", { reason: "no_url" });
      return;
    }
    
    console.log("[GiftPlayer] Audio URL:", audioUrl);
    setAudioError(null);

    fetch(audioUrl, { method: "HEAD" })
      .then(res => {
        console.log("[GiftPlayer] Audio HEAD status:", res.status, "Content-Type:", res.headers.get("content-type"));
        if (res.status === 403) {
          setAudioError("expired");
          trackEvent("gift_audio_play_error", { reason: "403_expired", url: audioUrl.substring(0, 60) });
        } else if (res.status === 404) {
          setAudioError("not_found");
          trackEvent("gift_audio_play_error", { reason: "404_not_found" });
        } else if (!res.ok) {
          setAudioError("unavailable");
          trackEvent("gift_audio_play_error", { reason: `http_${res.status}` });
        }
      })
      .catch(err => {
        console.error("[GiftPlayer] Audio HEAD error (CORS or network):", err);
        // CORS errors on HEAD don't necessarily mean the audio won't play
        // So we don't set error here, just log it
      });
  }, [audioUrl]);

  // Pre-fetch audio as blob for mobile compatibility (external URLs may not support Range requests)
  useEffect(() => {
    const isMobile = /Mobile|iPhone|iPod|iPad|Android/i.test(navigator.userAgent);
    const isExternal = audioUrl && !audioUrl.includes(window.location.hostname) && !audioUrl.includes('supabase.co');
    
    if (isMobile && isExternal && !blobSrc) {
      fetch(audioUrl)
        .then(res => {
          if (!res.ok) {
            console.error("[GiftPlayer] Blob fetch failed:", res.status, res.statusText);
            return null;
          }
          return res.blob();
        })
        .then(blob => {
          if (blob) {
            const url = URL.createObjectURL(blob);
            setBlobSrc(url);
            console.log("[GiftPlayer] Blob created successfully, size:", blob.size);
          }
        })
        .catch(err => console.warn("[GiftPlayer] blob prefetch failed:", err));
    }
    
    return () => {
      if (blobSrc) URL.revokeObjectURL(blobSrc);
    };
  }, [audioUrl]);

  const effectiveSrc = blobSrc || audioUrl;

  const togglePlay = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    if (isPlaying) {
      audio.pause();
      setIsPlaying(false);
    } else {
      const playPromise = audio.play();
      if (playPromise !== undefined) {
        playPromise
          .then(() => {
            setIsPlaying(true);
            setShowPulse(false);
            internalPlayCount.current += 1;
            onPlayCountChange?.(internalPlayCount.current);
            trackEvent("gift_play_clicked");
            if (!hasCalledFirstPlay.current) {
              hasCalledFirstPlay.current = true;
              onFirstPlay?.();
            }
          })
          .catch((err) => {
            console.warn("[GiftPlayer] play() rejected:", err);
            // If direct URL failed and we haven't tried blob yet, fetch as blob and retry
            if (!triedBlobFallback.current && !blobSrc) {
              triedBlobFallback.current = true;
              fetch(audioUrl)
                .then(res => res.blob())
                .then(blob => {
                  const url = URL.createObjectURL(blob);
                  setBlobSrc(url);
                  // Audio src will update via effect, user needs to tap again
                })
                .catch(() => {
                  setAudioError("play_failed");
                  trackEvent("gift_audio_play_error", { reason: "play_rejected_no_fallback" });
                });
            } else {
              setAudioError("play_failed");
              trackEvent("gift_audio_play_error", { reason: "play_rejected" });
            }
            setIsPlaying(false);
          });
      }
    }
  }, [isPlaying, onFirstPlay, onPlayCountChange, audioUrl, blobSrc]);

  // ... keep existing code (timeupdate, duration, seek, skip, formatTime listeners)

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const onTime = () => {
      setProgress(audio.currentTime);
      if (audio.duration && isFinite(audio.duration) && audio.duration !== duration) {
        setDuration(audio.duration);
      }
      if (audio.duration > 0) {
        onCompletionChange?.(Math.round((audio.currentTime / audio.duration) * 100));
      }
    };
    const onDuration = () => {
      if (audio.duration && isFinite(audio.duration)) {
        setDuration(audio.duration);
      }
    };
    const onEnd = () => {
      setIsPlaying(false);
      setProgress(0);
    };
    const onError = () => {
      console.error("[GiftPlayer] Audio element error:", audio.error);
      setAudioError("load_failed");
      setIsPlaying(false);
      trackEvent("gift_audio_play_error", { reason: "audio_element_error", code: audio.error?.code });
    };
    audio.addEventListener("timeupdate", onTime);
    audio.addEventListener("loadedmetadata", onDuration);
    audio.addEventListener("durationchange", onDuration);
    audio.addEventListener("canplay", onDuration);
    audio.addEventListener("ended", onEnd);
    audio.addEventListener("error", onError);
    return () => {
      audio.removeEventListener("timeupdate", onTime);
      audio.removeEventListener("loadedmetadata", onDuration);
      audio.removeEventListener("durationchange", onDuration);
      audio.removeEventListener("canplay", onDuration);
      audio.removeEventListener("ended", onEnd);
      audio.removeEventListener("error", onError);
    };
  }, [duration, onCompletionChange]);

  useEffect(() => {
    const t = setTimeout(() => setShowPulse(false), 3000);
    return () => clearTimeout(t);
  }, []);

  const handleSeek = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!audioRef.current || !duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const pct = (e.clientX - rect.left) / rect.width;
    audioRef.current.currentTime = pct * duration;
    setProgress(pct * duration);
  };

  const skip = (seconds: number) => {
    if (!audioRef.current) return;
    audioRef.current.currentTime = Math.max(0, Math.min(duration, audioRef.current.currentTime + seconds));
  };

  const formatTime = (s: number) => {
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60);
    return `${m}:${sec.toString().padStart(2, "0")}`;
  };

  const timeColor = isDark ? "rgba(255,255,255,0.35)" : "rgba(26,26,46,0.35)";
  const barBg = isDark ? "rgba(255,255,255,0.08)" : "rgba(26,26,46,0.07)";
  const controlColor = isDark ? "rgba(255,255,255,0.4)" : "rgba(26,26,46,0.3)";

  // Auto-repair: when error detected, show "updating" and auto-reload after delay
  if ((audioError && audioError !== "play_failed") || isRepairing) {
    return (
      <div className="w-full flex flex-col items-center gap-4 px-4 py-8 text-center">
        <div
          className="w-16 h-16 rounded-full flex items-center justify-center"
          style={{ background: isDark ? "rgba(212,175,55,0.1)" : "rgba(108,99,255,0.08)" }}
        >
          {isRepairing ? (
            <RefreshCw className="w-7 h-7 animate-spin" style={{ color: accent }} />
          ) : (
            <AlertCircle className="w-8 h-8" style={{ color: accent }} />
          )}
        </div>
        <div>
          <p
            className="text-[15px] font-medium mb-1"
            style={{ color: isDark ? "rgba(255,255,255,0.85)" : "#1A1A2E" }}
          >
            {isRepairing ? "Обновляем аудио..." : "Мы готовим ваш подарок"}
          </p>
          <p
            className="text-[13px]"
            style={{ color: isDark ? "rgba(255,255,255,0.45)" : "rgba(26,26,46,0.5)" }}
          >
            {isRepairing ? "Подождите пару секунд" : "Попробуйте обновить страницу через минуту"}
          </p>
        </div>
        {!isRepairing && (
          <>
            <button
              onClick={() => {
                if (repairAttempts.current < 3) {
                  repairAttempts.current++;
                  setIsRepairing(true);
                  setAudioError(null);
                  trackEvent("audio_auto_repair_triggered", { attempt: repairAttempts.current });
                  // Reload page after 3 seconds to pick up potentially repaired URL
                  setTimeout(() => window.location.reload(), 3000);
                } else {
                  window.location.reload();
                }
              }}
              className="flex items-center gap-2 px-5 py-2.5 rounded-full text-[13px] font-medium transition-all hover:scale-105 active:scale-95"
              style={{
                background: isDark ? "rgba(212,175,55,0.12)" : "rgba(108,99,255,0.08)",
                color: accent,
              }}
            >
              <RefreshCw className="w-4 h-4" />
              Обновить
            </button>
          </>
        )}
      </div>
    );
  }


  return (
    <div className="w-full flex flex-col items-center gap-0 px-1">
      <audio ref={audioRef} src={effectiveSrc} preload="auto" />

      {/* Album art / cover */}
      <div
        className="w-full aspect-square max-w-[220px] sm:max-w-[260px] rounded-[20px] relative overflow-hidden mb-4 sm:mb-6"
        style={{
          background: coverGradient || (isDark
            ? `linear-gradient(135deg, #1A1A30, #252540)`
            : `linear-gradient(135deg, #E8E6F0, #D5D2E8)`),
          boxShadow: isDark
            ? `0 20px 60px rgba(0,0,0,0.4), 0 0 30px ${accent}08`
            : `0 20px 60px rgba(108,99,255,0.12), 0 0 30px ${accent}06`,
        }}
      >
        {coverUrl ? (
          <img src={coverUrl} alt={trackTitle} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex flex-col items-center justify-center p-6">
            {/* Vinyl record visual */}
            <div
              className="w-[120px] h-[120px] rounded-full relative"
              style={{
                background: isDark
                  ? `conic-gradient(from 0deg, rgba(255,255,255,0.03), rgba(255,255,255,0.08), rgba(255,255,255,0.03), rgba(255,255,255,0.08), rgba(255,255,255,0.03))`
                  : `conic-gradient(from 0deg, rgba(0,0,0,0.04), rgba(0,0,0,0.08), rgba(0,0,0,0.04), rgba(0,0,0,0.08), rgba(0,0,0,0.04))`,
                animation: isPlaying ? "gift-vinyl-spin 8s linear infinite" : undefined,
              }}
            >
              <div
                className="absolute inset-[35%] rounded-full"
                style={{ background: accent, opacity: 0.7 }}
              />
              <div
                className="absolute inset-[46%] rounded-full"
                style={{ background: isDark ? "#0D0D14" : "#F5F5F7" }}
              />
            </div>
            <p className="text-white/80 text-[15px] font-semibold text-center mt-5 leading-snug px-2">
              {trackTitle}
            </p>
            {heroName && (
              <p className="text-white/40 text-[12px] mt-1">{heroName}</p>
            )}
          </div>
        )}

        {/* Playing overlay glow */}
        {isPlaying && (
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              background: `radial-gradient(circle at 50% 80%, ${accent}15, transparent 60%)`,
            }}
          />
        )}
      </div>

      {/* Track info below cover */}
      <div className="text-center mb-3 sm:mb-5 w-full">
        <p
          className="text-[17px] font-semibold leading-snug"
          style={{ color: isDark ? "rgba(255,255,255,0.92)" : "#1A1A2E" }}
        >
          {trackTitle}
        </p>
        {heroName && (
          <p
            className="text-[13px] mt-1"
            style={{ color: isDark ? "rgba(255,255,255,0.45)" : "rgba(26,26,46,0.45)" }}
          >
            {heroName}
          </p>
        )}
      </div>

      {/* Progress bar — always visible */}
      <div className="w-full mb-4">
        <div
          className="w-full h-[4px] rounded-full cursor-pointer group relative"
          style={{ background: barBg }}
          onClick={handleSeek}
        >
          <div
            className="h-full rounded-full transition-[width] duration-100 relative"
            style={{
              width: duration > 0 ? `${(progress / duration) * 100}%` : "0%",
              background: `linear-gradient(90deg, ${accent}, ${adjustColor(accent, 20)})`,
            }}
          >
            {/* Seek knob */}
            <div
              className="absolute right-0 top-1/2 -translate-y-1/2 w-[12px] h-[12px] rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
              style={{
                background: accent,
                boxShadow: `0 0 8px ${accent}60`,
              }}
            />
          </div>
        </div>
        <div className="flex justify-between mt-2">
          <span className="text-[11px] font-mono tabular-nums" style={{ color: timeColor }}>
            {formatTime(progress)}
          </span>
          <span className="text-[11px] font-mono tabular-nums" style={{ color: timeColor }}>
            {duration > 0 ? formatTime(duration) : "--:--"}
          </span>
        </div>
      </div>

      {/* Controls row */}
      <div className="flex items-center gap-6">
        <button
          onClick={() => skip(-10)}
          aria-label="Назад 10 сек"
          className="transition-all duration-200 hover:scale-110 active:scale-95"
          style={{ color: controlColor }}
        >
          <SkipBack className="w-5 h-5" />
        </button>

        {/* Main play button */}
        <div className="relative">
          {showPulse && (
            <div
              className="absolute inset-0 rounded-full pointer-events-none"
              style={{
                animation: "gift-play-pulse 2s ease-in-out infinite",
                background: `radial-gradient(circle, ${accent}20, transparent 70%)`,
                transform: "scale(2)",
              }}
            />
          )}
          {isPlaying && (
            <div
              className="absolute inset-0 rounded-full pointer-events-none"
              style={{
                boxShadow: `0 0 40px ${accent}30, 0 0 80px ${accent}10`,
                transform: "scale(1.15)",
                animation: "gift-play-glow 3s ease-in-out infinite",
              }}
            />
          )}
          <button
            onClick={togglePlay}
            aria-label={isPlaying ? "Пауза" : "Играть"}
            className="relative z-10 w-[64px] h-[64px] rounded-full flex items-center justify-center transition-all duration-300 hover:scale-105 active:scale-95"
            style={{
              background: `linear-gradient(135deg, ${accent}, ${adjustColor(accent, -35)})`,
              boxShadow: isPlaying
                ? `0 0 30px ${accent}35, 0 8px 24px ${accent}20`
                : `0 8px 24px ${accent}18`,
            }}
          >
            {isPlaying ? (
              <Pause className="w-6 h-6 text-white" />
            ) : (
              <Play className="w-6 h-6 text-white ml-0.5" />
            )}
          </button>
        </div>

        <button
          onClick={() => skip(10)}
          aria-label="Вперёд 10 сек"
          className="transition-all duration-200 hover:scale-110 active:scale-95"
          style={{ color: controlColor }}
        >
          <SkipForward className="w-5 h-5" />
        </button>
      </div>

      <style>{`
        @keyframes gift-play-pulse {
          0%, 100% { opacity: 0.4; transform: scale(1.6); }
          50% { opacity: 0.8; transform: scale(2.1); }
        }
        @keyframes gift-play-glow {
          0%, 100% { opacity: 0.6; }
          50% { opacity: 1; }
        }
        @keyframes gift-vinyl-spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default GiftPlayerCard;
